import React, { useEffect, useState } from 'react';
import { Button, Form, Select, Spin, Switch } from 'antd';
import { useTranslation } from 'react-i18next';

import { BasicFormWrapper } from 'container/styled';
import openNotificationWithIcon from 'utility/notification';
import { debtCollectionApi, DebtCollectionConfigDto } from '../debtCollectionApi';

const COMMON_TIMEZONES = [
  'UTC',
  'Europe/London',
  'Europe/Berlin',
  'Europe/Paris',
  'Europe/Rome',
  'Europe/Madrid',
  'Europe/Amsterdam',
  'Europe/Brussels',
  'Europe/Vienna',
  'Europe/Zurich',
  'Europe/Warsaw',
  'Europe/Prague',
  'Europe/Bucharest',
  'Europe/Sofia',
  'Europe/Athens',
  'Europe/Istanbul',
  'Europe/Moscow',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'America/Sao_Paulo',
  'Asia/Dubai',
  'Asia/Kolkata',
  'Asia/Shanghai',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
  'Pacific/Auckland',
];

interface ConfigTabProps {
  config: DebtCollectionConfigDto | null;
  loading: boolean;
  onConfigSaved: () => void;
}

const ConfigTab: React.FC<ConfigTabProps> = ({ config, loading, onConfigSaved }) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (config) {
      form.setFieldsValue({
        isEnabled: config.isEnabled ?? false,
        timeZoneId: config.timeZoneId ?? 'UTC',
      });
    }
  }, [config, form]);

  const handleSubmit = async (values: { isEnabled: boolean; timeZoneId: string }) => {
    setSaving(true);
    try {
      if (config?.id) {
        await debtCollectionApi.updateConfig({
          id: config.id,
          isEnabled: values.isEnabled,
          timeZoneId: values.timeZoneId,
        });
      } else {
        await debtCollectionApi.createConfig({
          isEnabled: values.isEnabled,
          timeZoneId: values.timeZoneId,
        });
      }
      openNotificationWithIcon(
        'success',
        t('debt-collection:config.saved', 'Configuration saved successfully'),
      );
      onConfigSaved();
    } catch (error) {
      console.error('Error saving config:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', padding: 60 }}>
        <Spin size="large" />
      </div>
    );
  }

  return (
    <BasicFormWrapper>
      <Form
        form={form}
        layout="vertical"
        onFinish={handleSubmit}
        initialValues={{ isEnabled: false, timeZoneId: 'UTC' }}
        style={{ maxWidth: 500 }}
      >
        <Form.Item
          label={t('debt-collection:config.enabled', 'Enable Debt Collection')}
          name="isEnabled"
          valuePropName="checked"
        >
          <Switch />
        </Form.Item>

        <Form.Item
          label={t('debt-collection:config.timezone', 'Organization Timezone')}
          name="timeZoneId"
          rules={[{ required: true, message: t('debt-collection:config.timezone-required', 'Timezone is required') }]}
        >
          <Select
            showSearch
            placeholder={t('debt-collection:config.select-timezone', 'Select timezone')}
            optionFilterProp="children"
          >
            {COMMON_TIMEZONES.map((tz) => (
              <Select.Option key={tz} value={tz}>
                {tz}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>

        <Form.Item>
          <Button type="primary" htmlType="submit" loading={saving}>
            {config?.id
              ? t('global:update', 'Update')
              : t('global:create', 'Create')}
          </Button>
        </Form.Item>
      </Form>
    </BasicFormWrapper>
  );
};

export default ConfigTab;
